import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldCheck, RefreshCw, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { usePortalAuth } from "../../context/PortalAuthContext";
import PortalShell from "./PortalShell";
import SEO from "../../components/SEO";

export default function PortalSecurity() {
  const { customer, loading, resetTotp, logout } = usePortalAuth();
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (loading) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-500" />
        </div>
      </PortalShell>
    );
  }
  if (!customer) return <Navigate to="/portal/login" replace />;

  const doReset = async () => {
    setSubmitting(true);
    setError("");
    try {
      await resetTotp();
      setSuccess(true);
      // After a few seconds, log them out so they re-setup TOTP on next login.
      setTimeout(() => { logout(); }, 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not reset 2FA. Try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalShell>
      <SEO title="Security · Tech Emergency portal" path="/portal/security" noIndex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="portal-security">
        <div className="te-overline mb-3">/ Security</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-2">
          Two-factor authentication
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          Your account is protected by a 6-digit code from an authenticator app
          on top of your password.
        </p>

        <div className="te-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ background: "var(--te-success, #22c55e)" }}>
              <ShieldCheck size={18} color="#fff" />
            </div>
            <div>
              <div className="font-semibold tracking-tight">2FA is active</div>
              <div className="text-xs text-zinc-500">Required at every sign-in.</div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-4">
            <div className="te-overline text-[10px] mb-1.5 text-zinc-500">Lost your device?</div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-3">
              Reset your authenticator. We&apos;ll sign you out, and on your next
              login you&apos;ll scan a fresh QR code with your new device.
            </p>

            {success && (
              <div className="text-sm px-3 py-2 border border-green-500/30 bg-green-500/10 text-green-300 flex items-start gap-2" data-testid="portal-security-success">
                <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
                <span>2FA reset. Signing you out — log back in to scan a new QR.</span>
              </div>
            )}

            {error && (
              <div className="text-sm px-3 py-2 border border-red-500/30 bg-red-500/10 text-red-300 flex items-start gap-2" data-testid="portal-security-error">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {!confirming && !success && (
              <button
                onClick={() => setConfirming(true)}
                className="te-btn te-btn-ghost text-sm"
                data-testid="portal-security-reset-btn"
              >
                <RefreshCw size={14} /> Reset 2FA
              </button>
            )}

            {confirming && !success && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={doReset}
                  disabled={submitting}
                  className="te-btn te-btn-primary text-sm disabled:opacity-50"
                  data-testid="portal-security-reset-confirm"
                >
                  {submitting ? "Resetting…" : "Yes, reset my 2FA"}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  className="te-btn te-btn-ghost text-sm"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
          Still locked out? Call us on{" "}
          <a href="tel:1800836390" className="text-white hover:text-[var(--te-primary)] transition">
            1800 836 390
          </a>{" "}
          and we&apos;ll reset it for you over the phone.
        </p>
      </div>
    </PortalShell>
  );
}
